import { istek } from './istemci';
import type { Semalar } from './sema';
import type { Kimlik } from './tipler';

/**
 * DÖNEM UÇLARI (19).
 *
 * "DÖNEM KİLİTLENEBİLDİ Mİ" PİLOTUN EN SERT ÖLÇÜTÜDÜR ve blokerlerin tamamı
 * orada patlar.
 *
 * AŞAMALAR SIRAYLA İLERLER, atlanamaz ve GERİYE ALINAMAZ:
 *   acik → on_kapanis → itiraz_penceresi → kesin_kapanis → kilitli
 *
 * PARASAL VE SAAT TOPLAMLARI METİNDİR. Kayan noktaya çevrilirse aylık toplamda
 * kuruş kaybı birikir ve mutabakat farkı YAPAY OLARAK BÜYÜR.
 *
 * YETKİ DAĞILIMI — yedi ayrı liste:
 *   GET  /donem · /:id            PERSONEL_GORUNTULE | DONEM_KAPAT | RAPOR_GORUNTULE
 *   GET  /:id/kilit-kontrolu      DONEM_KAPAT                          ← TEK
 *   PATCH dört aşama              DONEM_KAPAT                          ← TEK
 *   GET  /:id/mutabakat · trend   MUTABAKAT_GIR | DONEM_KAPAT | RAPOR_GORUNTULE
 *   POST /:id/mutabakat           MUTABAKAT_GIR | DONEM_KAPAT
 *   PATCH /:id/mutabakat          MUTABAKAT_GIR                        ← TEK
 *   GET  /:id/goruntuleme-orani   DONEM_KAPAT | RAPOR_GORUNTULE
 *   GET  /itiraz/liste            ITIRAZ_KARAR | RAPOR_GORUNTULE
 *   PATCH itiraz incele · karar   ITIRAZ_KARAR                         ← TEK
 *   POST itiraz/:id/yeniden-hesapla   ITIRAZ_KARAR | PUANTAJ_DUZELT
 *   POST /itiraz · GET /itiraz/benim  ITIRAZ_ET
 *   POST /:id/ozet-goruntulendi   PUANTAJ_GORUNTULE
 */

export type DonemOzeti = Semalar['DonemOzetiDto'];
export type KilitKontrolu = Semalar['KilitKontroluDto'];
export type ItirazPenceresiSonucu = Semalar['ItirazPenceresiSonucuDto'];
export type GoruntulemeOrani = Semalar['GoruntulemeOraniDto'];
export type MutabakatOzeti = Semalar['MutabakatOzetiDto'];
export type MutabakatTrendSatiri = Semalar['MutabakatTrendSatiriDto'];
export type ItirazOzeti = Semalar['ItirazOzetiDto'];
export type ItirazYenidenHesapSonucu = Semalar['ItirazYenidenHesapSonucuDto'];

export type DonemDurumu = DonemOzeti['durum'];
export type KilitSeviyesi = KilitKontrolu['seviye'];
export type ItirazDurumu = ItirazOzeti['durum'];
export type FarkSebebi = NonNullable<MutabakatOzeti['farkSebebi']>;

export const DURUM_ETIKETI: Record<DonemDurumu, string> = {
  acik: 'Açık',
  on_kapanis: 'Ön kapanış',
  itiraz_penceresi: 'İtiraz penceresi',
  kesin_kapanis: 'Kesin kapanış',
  kilitli: 'Kilitli',
};

/** Aşama sırası — düğme yalnız SIRADAKİ aşama için gösterilir. */
export const DURUM_SIRASI: DonemDurumu[] = [
  'acik',
  'on_kapanis',
  'itiraz_penceresi',
  'kesin_kapanis',
  'kilitli',
];

export const ITIRAZ_DURUM_ETIKETI: Record<ItirazDurumu, string> = {
  acik: 'Açık',
  inceleniyor: 'İnceleniyor',
  kabul: 'Kabul',
  red: 'Red',
  iptal: 'İptal',
};

export const FARK_SEBEBI_ETIKETI: Record<FarkSebebi, string> = {
  kapsam: 'Kapsam farkı',
  katsayi: 'Katsayı farkı',
  muhasebe_duzeltmesi: 'Muhasebe düzeltmesi',
  sistem_hatasi: 'Sistem hatası',
  diger: 'Diğer',
};

/* ------------------------------------------------------------------ */

export function donemListesi(): Promise<DonemOzeti[]> {
  return istek<DonemOzeti[]>('/v1/donem');
}

export function donemAyrintisi(donemId: number): Promise<DonemOzeti> {
  return istek<DonemOzeti>(`/v1/donem/${donemId}`);
}

/**
 * Kilit ön koşulları (`fn_donem_kilit_kontrol`).
 *
 * `engel` GEÇİLEMEZ · `ik_onayi` açık onayla geçilir. İkisi AYRI seviye;
 * tek listede toplansaydı geçilemeyen bir engel "onayla ve devam et"
 * sanılırdı.
 */
export function kilitKontrolu(donemId: number): Promise<KilitKontrolu[]> {
  return istek<KilitKontrolu[]>(`/v1/donem/${donemId}/kilit-kontrolu`);
}

/* --- Dört aşamalı kapanış (hepsi DONEM_KAPAT) ------------------------ */

export function onKapanis(donemId: number, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/donem/${donemId}/on-kapanis`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

/** Pencere kapanış tarihi `ITIRAZ_SURESI_IS_GUNU` parametresinden hesaplanır. */
export function itirazPenceresiAc(
  donemId: number,
  aciklama?: string,
): Promise<ItirazPenceresiSonucu> {
  return istek<ItirazPenceresiSonucu>(`/v1/donem/${donemId}/itiraz-penceresi`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

/** GÖREVLER AYRILIĞI: ön kapanışı yapanla AYNI kişi olamaz (sunucu denetler). */
export function kesinKapanis(donemId: number, aciklama?: string): Promise<void> {
  return istek<void>(`/v1/donem/${donemId}/kesin-kapanis`, {
    yontem: 'PATCH',
    govde: { aciklama },
  });
}

/**
 * Dönemi kilitler — GERİ ALINAMAZ.
 *
 * `ikOnayi` 270 saat aşımı varsa ZORUNLU: onaysız kilit, aşımın fark
 * edilmeden bordroya girmesi demektir (4857/41). Gerekçe de zorunlu ve
 * kalıcı kayıttır.
 */
export function donemKilitle(donemId: number, ikOnayi: boolean, gerekce: string): Promise<void> {
  return istek<void>(`/v1/donem/${donemId}/kilitle`, {
    yontem: 'PATCH',
    govde: { ikOnayi, gerekce },
  });
}

/* --- Mutabakat ------------------------------------------------------- */

export function mutabakatOzeti(donemId: number): Promise<MutabakatOzeti> {
  return istek<MutabakatOzeti>(`/v1/donem/${donemId}/mutabakat`);
}

/**
 * Mutabakat kaydını açar — SİSTEM TUTARINI DONDURUR.
 *
 * Muhasebe tutarı SONRA girilir. Ters sırada olsaydı sistem tutarı
 * muhasebeye göre "düzeltilebilir" ve mutabakat anlamını yitirirdi.
 */
export function mutabakatAc(donemId: number): Promise<MutabakatOzeti> {
  return istek<MutabakatOzeti>(`/v1/donem/${donemId}/mutabakat`, { yontem: 'POST' });
}

/**
 * Muhasebe tutarını girer.
 *
 * BRÜT tutar — net karşılaştırma her dönem yanlış alarm üretir. Biçim
 * sunucuda `^\d{1,14}(\.\d{1,2})?$` ile denetleniyor; panel de aynı deseni
 * uyguluyor ki kullanıcı 400 yerine anında uyarı görsün.
 */
export function muhasebeTutariGir(
  donemId: number,
  muhasebeBrutMesai: string,
  farkSebebi?: FarkSebebi,
  aciklama?: string,
): Promise<MutabakatOzeti> {
  return istek<MutabakatOzeti>(`/v1/donem/${donemId}/mutabakat`, {
    yontem: 'PATCH',
    govde: { muhasebeBrutMesai, farkSebebi, aciklama },
  });
}

/**
 * Mutabakat trendi.
 *
 * FARK HER AY AYNI YÖNDE İSE SİSTEMATİK HATA VAR ve bulunabilir. Hiç
 * ölçülmezse sistem yıllarca yanlış rakam üretir.
 */
export function mutabakatTrendi(): Promise<MutabakatTrendSatiri[]> {
  return istek<MutabakatTrendSatiri[]>('/v1/donem/mutabakat/trend');
}

/**
 * Dönem özetini kaç kişi gördü.
 *
 * Oran düşükse itiraz yokluğu "herkes memnun" DEĞİL "kimse bakmadı" demektir;
 * ikisi karıştırılırsa pilot yanlış okunur.
 */
export function goruntulemeOrani(donemId: number): Promise<GoruntulemeOrani> {
  return istek<GoruntulemeOrani>(`/v1/donem/${donemId}/goruntuleme-orani`);
}

/** "Bilgilendirildi" kaydı — PWA özeti açtığında çağırır. */
export function ozetGoruntulendi(donemId: number): Promise<void> {
  return istek<void>(`/v1/donem/${donemId}/ozet-goruntulendi`, { yontem: 'POST' });
}

/* --- İtiraz ---------------------------------------------------------- */

export interface ItirazSorgusu {
  donemId?: number;
  atamaId?: Kimlik;
  durum?: ItirazDurumu;
}

export function itirazListesi(sorgu: ItirazSorgusu = {}): Promise<ItirazOzeti[]> {
  return istek<ItirazOzeti[]>('/v1/donem/itiraz/liste', { sorgu: { ...sorgu } });
}

export function itirazlarim(): Promise<ItirazOzeti[]> {
  return istek<ItirazOzeti[]>('/v1/donem/itiraz/benim');
}

export interface ItirazAcGirdisi {
  puantajTarihi: string;
  gerekce: string;
  talepEdilenGiris?: string;
  talepEdilenCikis?: string;
}

export function itirazAc(girdi: ItirazAcGirdisi): Promise<ItirazOzeti> {
  return istek<ItirazOzeti>('/v1/donem/itiraz', { yontem: 'POST', govde: girdi });
}

export function itirazIncelemeyeAl(itirazId: Kimlik): Promise<void> {
  return istek<void>(`/v1/donem/itiraz/${itirazId}/incele`, { yontem: 'PATCH' });
}

/**
 * İtirazı karara bağlar.
 *
 * GÖREVLER AYRILIĞI: kendi itirazını karara bağlayamazsın (sunucu denetler).
 * Gerekçe ZORUNLU — gerekçesiz red personelin ikinci kez itiraz etmesine yol
 * açar, gerekçesiz kabul denetimde "bu düzeltme neye dayanıyor" sorusunu
 * cevapsız bırakır.
 */
export function itirazKarar(
  itirazId: Kimlik,
  kabul: boolean,
  kararGerekcesi: string,
): Promise<void> {
  return istek<void>(`/v1/donem/itiraz/${itirazId}/karar`, {
    yontem: 'PATCH',
    govde: { kabul, kararGerekcesi },
  });
}

/**
 * Kabul edilen itiraz sonrası günü yeniden hesaplar.
 *
 * AYRI ADIMDIR: düzeltme devam hareketi üzerinden yapıldıktan SONRA çağrılır.
 * Karar anında çağrılsaydı henüz düzeltilmemiş veriyi hesaplar ve hiçbir şey
 * değişmezdi.
 */
export function itirazYenidenHesapla(itirazId: Kimlik): Promise<ItirazYenidenHesapSonucu> {
  return istek<ItirazYenidenHesapSonucu>(`/v1/donem/itiraz/${itirazId}/yeniden-hesapla`, {
    yontem: 'POST',
  });
}
